import { useEffect, useRef, useSyncExternalStore } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { useUserProfile } from "./user-profile-provider";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useBillingSession } from "@/lib/billing-session";
import { dismissAccessError, getAccessErrorSnapshot, getAccessErrorReturnFocus, getOrganizationAccessError, getOrganizationAccessMessage, getOrganizationAccessTitle, handleOrganizationAccessError, setAccessErrorScope, subscribeToAccessErrors } from "@/lib/organization-access-errors";

export function OrganizationAccessDialog() {
  const { userProfile } = useUserProfile();
  const returnFocus = useRef<HTMLElement | null>(null);
  const access = useSyncExternalStore(subscribeToAccessErrors, getAccessErrorSnapshot, () => null);
  const canManageBilling = userProfile?.orgRole === "owner" || userProfile?.orgRole === "admin";
  // Billing can resolve these states; others (suspension, unknown) are not a billing problem.
  const isBillingState = access?.state === "unpaid_setup" || access?.state === "payment_grace" || access?.state === "expired";
  const organizationId = userProfile?.organizationId ?? null;
  // Server-chosen Checkout or Portal in a new tab, so the blocked work stays open here.
  const billing = useBillingSession({ newTab: true });

  useEffect(() => {
    setAccessErrorScope(organizationId);
    return () => setAccessErrorScope(null);
  }, [organizationId]);

  useEffect(() => {
    // Legacy fire-and-forget handlers still reject; consume only this expected denial.
    const onUnhandled = (event: PromiseRejectionEvent) => {
      if (!getOrganizationAccessError(event.reason)) return;
      handleOrganizationAccessError(event.reason);
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", onUnhandled);
    return () => window.removeEventListener("unhandledrejection", onUnhandled);
  }, []);

  return (
    <Dialog open={access !== null} onOpenChange={(open) => { if (!open) dismissAccessError(); }}>
      <DialogContent
        draggable={false}
        overlayClassName="z-[160]"
        className="z-[161]"
        onOpenAutoFocus={() => {
          returnFocus.current = getAccessErrorReturnFocus();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const target = returnFocus.current;
          if (target?.isConnected && target !== document.body) target.focus();
          else document.querySelector<HTMLElement>('[role="dialog"][data-state="open"] input')?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{getOrganizationAccessTitle(access?.state ?? "unavailable")}</DialogTitle>
          <DialogDescription>
            {getOrganizationAccessMessage(access?.state ?? "unavailable")}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">You can still view your projects and download completed results.</p>
        {isBillingState ? (
          <p className="text-sm">
            {canManageBilling
              ? "Open billing to start or update your subscription. This page stays open, so you won't lose your work."
              : "Ask your company's owner or admin to set up billing."}
          </p>
        ) : access?.state !== "suspended" ? (
          <p className="text-sm">Contact your company's owner or admin for help.</p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={dismissAccessError}>Dismiss</Button>
          {canManageBilling && isBillingState && (
            <Button onClick={() => void billing.open()} disabled={billing.pendingIntent !== null}>
              {billing.pendingIntent !== null
                ? <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                : <ExternalLink className="size-4" aria-hidden="true" />}
              Open billing
              <span className="sr-only">, opens in a new tab</span>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
